/* eslint-disable better-mutation/no-mutation */
import { createSlice } from '@reduxjs/toolkit';
import { MODAL_STATE, ITEM_LAYOUT } from 'tradera/constants/ui';
import { cache, CACHE_KEYS } from 'tradera/utils/cache';
import { getSystemMessageAlertCount } from './helpers';
import { getInitialBadges, type UiState } from './initial-state';

const MESSAGING_NOTIFICATIONS_CACHE_TTL = 30 * 1000;

const uiSlice = createSlice({
    name: 'ui',
    initialState: {
        itemLayout: ITEM_LAYOUT.LIST
    } as UiState,
    reducers: {
        initialize: (_state, { payload }) => {
            return {
                ...payload
            };
        },
        toggleHeaderSearch: (state, { payload }) => {
            state.showHeaderSearch = payload;
        },
        openQuickView: (state, { payload }) => {
            const {
                items = state.quickView.items,
                item = state.quickView.item
            } = payload;

            state.quickView = {
                ...state.quickView,
                open: MODAL_STATE.OPEN,
                items: items,
                item: item
            };
        },
        closeQuickView: (state) => {
            state.quickView.open = MODAL_STATE.CLOSED;
        },
        loadingQuickView: (state, { payload }) => {
            state.quickView.loading = payload;
        },
        openVideoModal: (state, { payload }) => {
            const { videoUrl, title, pageType, buttonLink, buttonText } =
                payload;
            state.videoModal = {
                ...state.videoModal,
                open: MODAL_STATE.OPEN,
                videoUrl,
                title,
                pageType,
                buttonLink,
                buttonText
            };
        },
        closeVideoModal: (state) => {
            state.videoModal.open = MODAL_STATE.CLOSED;
        },
        openYoutubeVideoModal: (state, { payload }) => {
            const { videoId, title, pageType } = payload;
            state.youtubeVideoModal = {
                ...state.youtubeVideoModal,
                open: MODAL_STATE.OPEN,
                videoId: videoId,
                title: title,
                pageType: pageType
            };
        },
        closeYoutubeVideoModal: (state) => {
            state.youtubeVideoModal.open = MODAL_STATE.CLOSED;
        },
        /*
            Changes made to messaging modal will affect My Tradera as well!
            TODO - test this!
        */
        openMessagingModal: (state, { payload }) => {
            const {
                title = state.messagingModal.title,
                src = state.messagingModal.src,
                participating = state.messagingModal.participating,
                recipientId = state.messagingModal.recipientId,
                item = state.messagingModal.item
            } = payload;
            state.messagingModal = {
                ...state.messagingModal,
                open: MODAL_STATE.OPEN,
                title: title,
                src: src,
                participating: participating,
                recipientId: recipientId,
                item: item,
                isContactHighestBidder: payload.isContactHighestBidder
            };
        },
        closeMessagingModal: (state) => {
            state.messagingModal.open = MODAL_STATE.CLOSED;
        },
        openNotificationOptinModal: (state, { payload }) => {
            const { modalType, gaLabel, title, description, tagGroups } =
                payload;
            state.notificationOptinModal = {
                ...state.notificationOptinModal,
                open: MODAL_STATE.OPEN,
                modalType,
                gaLabel,
                title,
                description,
                tagGroups
            };
        },
        closeNotificationOptinModal: (state) => {
            state.notificationOptinModal.open = MODAL_STATE.CLOSED;
        },
        openModal: (state, { payload }) => {
            const { modalId, data } = payload;
            state.modals = {
                currentModal: modalId,
                currentModalData: data
            };
        },
        closeModal: (state) => {
            state.modals = {
                currentModal: null,
                currentModalData: null
            };
        },
        itemLayout: (state, { payload }) => {
            state.itemLayout = payload;
        },
        siteLayout: (state, { payload }) => {
            state.siteLayout = payload;
        },
        setSystemMessages: (state, { payload }) => {
            state.systemMessages = payload;
            state.badges.system = getSystemMessageAlertCount(payload);
        },
        updateSystemMessages: (state, { payload }) => {
            state.systemMessages = {
                ...state.systemMessages,
                ...payload
            };
            state.badges.system = getSystemMessageAlertCount(payload);
        },
        updateMessagingNotifications: (state, { payload }) => {
            cache.write(
                CACHE_KEYS.MESSAGING_BADGES,
                payload,
                MESSAGING_NOTIFICATIONS_CACHE_TTL
            );
            state.badges.messaging = payload;
        },
        openProfileMenu: (state, { payload }) => {
            state.profileMenu = {
                open: MODAL_STATE.OPEN,
                redirectUrl: payload || false
            };
        },
        closeProfileMenu: (state) => {
            state.profileMenu.open = MODAL_STATE.CLOSED;
        },
        openCategoryDropdownMenu: (state) => {
            state.categoryDropdownMenu.open = MODAL_STATE.OPEN;
        },
        closeCategoryDropdownMenu: (state) => {
            state.categoryDropdownMenu.open = MODAL_STATE.CLOSED;
        },
        openSearchForm: (state) => {
            state.showSearchForm = true;
        },
        closeSearchForm: (state) => {
            state.showSearchForm = false;
        },
        logOut: (state) => {
            state.badges = getInitialBadges();
        },
        openItemSettings: (state, { payload }) => {
            state.itemSettings = {
                open: MODAL_STATE.OPEN,
                item: payload
            };
        },
        closeItemSettings: (state) => {
            state.itemSettings = {
                open: MODAL_STATE.CLOSED,
                item: {}
            };
        },
        openShareModal: (state, { payload }) => {
            state.shareModal = {
                isOpen: true,
                settings: payload
            };
        },
        closeShareModal: (state) => {
            state.shareModal = {
                isOpen: false,
                settings: null
            };
        },
        openHelpSection: (state, { payload }) => {
            state.helpSection = {
                isOpen: true,
                settings: payload
            };
        },
        closeHelpSection: (state) => {
            state.helpSection = {
                isOpen: false,
                settings: null
            };
        },
        openWishListSideSheet: (state) => {
            state.wishListSideSheet.isOpen = true;
        },
        closeWishListSideSheet: (state) => {
            state.wishListSideSheet.isOpen = false;
        },
        updateProductCommunications: (state, { payload }) => {
            state.productCommunications = payload;
        },
        showSaveSearchBanner: (state, { payload = true }) => {
            state.showSaveSearchBanner = payload;
        },
        openCollectSsnModal: (state) => {
            state.collectSsnModal.isOpen = true;
        },
        closeCollectSsnModal: (state) => {
            state.collectSsnModal.isOpen = false;
        }
    }
});

export const {
    initialize,
    toggleHeaderSearch,
    openQuickView,
    closeQuickView,
    loadingQuickView,
    openVideoModal,
    closeVideoModal,
    openYoutubeVideoModal,
    closeYoutubeVideoModal,
    openMessagingModal,
    closeMessagingModal,
    openNotificationOptinModal,
    closeNotificationOptinModal,
    itemLayout,
    setSystemMessages,
    updateSystemMessages,
    updateMessagingNotifications,
    openProfileMenu,
    closeProfileMenu,
    openCategoryDropdownMenu,
    closeCategoryDropdownMenu,
    openSearchForm,
    closeSearchForm,
    openItemSettings,
    closeItemSettings,
    siteLayout,
    logOut,
    openModal,
    closeModal,
    openShareModal,
    closeShareModal,
    openHelpSection,
    closeHelpSection,
    openWishListSideSheet,
    closeWishListSideSheet,
    updateProductCommunications,
    showSaveSearchBanner,
    openCollectSsnModal,
    closeCollectSsnModal
} = uiSlice.actions;

export const reducer = uiSlice.reducer;
